import { useState } from 'react'
import { toast } from './useToast'
import {
  downloadDocument,
  downloadVisualRepresentation,
} from '@/services/backendApiRest'

// Shared utility functions for download operations

export function getFilenameFromContentDisposition(
  contentDispositionHeader: string | null | undefined,
  defaultFileName: string,
): string {
  if (!contentDispositionHeader) return defaultFileName

  const filenameStarMatch = contentDispositionHeader.match(
    /filename\*=UTF-8''([^;\s]+)/i,
  )
  if (filenameStarMatch?.[1]) {
    try {
      return decodeURIComponent(filenameStarMatch[1])
    } catch {
      // fall through to filename=
    }
  }

  const quotedFilenameMatch =
    contentDispositionHeader.match(/filename="([^"]+)"/i)
  if (quotedFilenameMatch?.[1]) return quotedFilenameMatch[1]

  const unquotedFilenameMatch =
    contentDispositionHeader.match(/filename=([^;\s]+)/i)
  if (unquotedFilenameMatch?.[1]) {
    return unquotedFilenameMatch[1].replace(/^"|"$/g, '')
  }

  return defaultFileName
}

export function downloadBlobAsFile(blob: Blob, fileName: string): void {
  const anchor = document.createElement('a')
  anchor.href = URL.createObjectURL(blob)
  anchor.setAttribute('download', fileName)
  anchor.click()
}

export function readBlobAsText(blob: Blob): Promise<string | undefined> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      const readerResult = reader.result
      resolve(typeof readerResult === 'string' ? readerResult : undefined)
    }
    reader.onerror = () => resolve(undefined)
    reader.readAsText(blob)
  })
}

export function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

type GenericDocumentDownloadType = 'document' | 'visual-representation'

type GenericDocumentDownload = {
  id: string
  shouldDownload?: boolean
  securityCode?: string | undefined
  type?: GenericDocumentDownloadType | undefined
}

export async function handleGenericDocumentDownload(
  props: GenericDocumentDownload,
): Promise<string | undefined> {
  try {
    const { id, shouldDownload, securityCode, type = 'document' } = props

    const downloaderMap = {
      'visual-representation': downloadVisualRepresentation,
      document: downloadDocument,
    } satisfies Record<GenericDocumentDownloadType, unknown>
    const downloader = downloaderMap[type]

    const response = await downloader(
      id,
      securityCode ? { 'x-security-code': securityCode } : undefined,
    )
    console.log({ downloadDocumentResponse: response })

    const blob = response.data

    if (!isSuccessStatus(response.status)) {
      toast({ title: 'Não foi possível realizar o download' })
      return undefined
    }

    if (shouldDownload) {
      const contentDispositionHeader = response.headers[
        'content-disposition'
      ] as string | undefined

      const fileName = getFilenameFromContentDisposition(
        contentDispositionHeader,
        'documento.xml',
      )

      downloadBlobAsFile(blob, fileName)
    }

    const xmlContent = await readBlobAsText(blob)

    return xmlContent
  } catch (error) {
    console.error({ handleDocumentAcademicDocumentationDownloadError: error })
    toast({ title: 'Não foi possível realizar o download' })
    return undefined
  }
}

export const useGenericDocumentDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false)

  async function downloadGenericDocument(props: GenericDocumentDownload) {
    setIsDownloading(true)
    const res = await handleGenericDocumentDownload(props)
    setIsDownloading(false)
    return res
  }

  return {
    downloadGenericDocument,
    isDownloading,
  }
}
